import { motion } from "framer-motion";
import { Quote, Lightbulb, Award, BookOpen, CheckCircle, RotateCcw } from "lucide-react";
import { Button } from "../components/ui/button";

const MASCOT_IMAGE = "https://customer-assets.emergentagent.com/job_a00bdaaa-d343-44f1-adb9-f8b11a8d38bf/artifacts/m964dse2_9C7E8ACF-CDB9-4A1C-A556-DF501523912B.jpeg";

export function SectionContent({ section, analogy, loadingAnalogy, generateAnalogy, foundEasterEgg, collectEasterEgg, onTakeQuiz, onScroll, quizCompleted, onResetQuiz }) {
  return (
    <motion.div
      key="content"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      {/* Section Header */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-2">
          <span className="badge-orange">Module {section.module}</span>
          <span className="text-zinc-500 text-sm">Section {section.id} of 21</span>
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-4">
          {section.title}
        </h1>
        <p className="text-xl text-[#F7931A] italic">
          "{section.hook}"
        </p>
      </div>

      <div className="prose prose-invert max-w-none mb-8">
        <p className="text-zinc-300 text-lg leading-relaxed whitespace-pre-line">
          {section.content}
        </p>
      </div>

      {section.influence_card && (
        <div className="bg-[#0A0A0A] border-l-4 border-[#F7931A] p-6 rounded-r-xl mb-8">
          <Quote className="text-[#F7931A] mb-3" size={24} />
          <p className="text-white text-lg italic mb-3">"{section.influence_card.quote}"</p>
          <p className="text-[#F7931A] font-medium">— {section.influence_card.name}</p>
        </div>
      )}

      <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-6 mb-8">
        <div className="flex items-center gap-2 mb-4">
          <Lightbulb className="text-[#F7931A]" size={20} />
          <h3 className="font-heading text-lg font-bold text-white">Bitcoin Brain Analogy</h3>
        </div>
        {analogy ? (
          <p className="text-zinc-300">{analogy}</p>
        ) : (
          <Button onClick={generateAnalogy} disabled={loadingAnalogy} className="btn-secondary" data-testid="generate-analogy-btn">
            {loadingAnalogy ? "Generating..." : "Generate Analogy"}
          </Button>
        )}
        {analogy && (
          <Button onClick={() => generateAnalogy(true)} variant="ghost" className="mt-3 text-[#F7931A]">
            Give me another
          </Button>
        )}
      </div>

      <div className="bg-gradient-to-br from-[#F7931A]/10 to-transparent border border-[#F7931A]/30 rounded-xl p-6 mb-8">
        <div className="flex items-start gap-4">
          <img src={MASCOT_IMAGE} alt="Bryce" className="w-12 h-12 rounded-lg flex-shrink-0" />
          <div>
            <h3 className="font-heading text-lg font-bold text-white mb-2">Reality Check</h3>
            <p className="text-zinc-300 italic">{section.reality_check}</p>
            <p className="text-zinc-500 text-sm mt-2">— Bryce Fields</p>
          </div>
        </div>
      </div>

      <div className="text-center py-8">
        {quizCompleted ? (
          <>
            <div className="flex items-center justify-center gap-2 text-green-400 mb-3">
              <CheckCircle size={18} />
              <span className="text-sm font-medium">Quiz completed</span>
            </div>
            <div className="flex justify-center gap-3">
              <Button onClick={onTakeQuiz} variant="ghost" className="border border-[#F7931A]/30 text-[#F7931A] hover:bg-[#F7931A]/10" data-testid="retake-quiz-btn">
                <RotateCcw className="mr-2" size={16} /> Retake Quiz
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className="text-zinc-500 mb-4">Ready to test your understanding? Complete the quiz to earn BTC XP!</p>
            <Button onClick={onTakeQuiz} className="btn-primary text-lg px-8 py-4" data-testid="take-quiz-btn">
              <BookOpen className="mr-2" size={20} /> Take Section Quiz
            </Button>
          </>
        )}
      </div>

      {!foundEasterEgg && section.hidden_fragment && (
        <div className="mt-32 pt-8 border-t border-white/5">
          <p className="text-zinc-800 text-sm cursor-pointer hover:text-zinc-600 transition-colors" onClick={collectEasterEgg}>
            {section.hidden_fragment}
          </p>
        </div>
      )}
      
      {foundEasterEgg && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-8 p-6 bg-[#F7931A]/10 border border-[#F7931A]/30 rounded-xl">
          <div className="flex items-center gap-2 mb-2">
            <Award className="text-[#F7931A]" size={20} />
            <span className="text-[#F7931A] font-bold">Hidden Fragment Discovered!</span>
          </div>
          <p className="text-zinc-300 italic whitespace-pre-line">"{section.hidden_fragment}"</p>
        </motion.div>
      )}
    </motion.div>
  );
}
